import React from 'react';
import { z } from 'zod';
import moment from 'moment';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@apollo/client';
import { CustomDialog } from 'components/shared/Dialog';
import { SelectField, DateField } from 'components/form';
import { Button, Stack, Typography, Divider } from '@mui/material';

import { bpfAPI } from '../../../api';
import { BpfCloseReasonType } from '../../../types';
import { Q_VO } from '../../../../../queries/common';
import { useDealLifeCycle } from '../../../api/dealLifeCycle';

const closeDealWithReasonFormSchema = z.object({
  reasonId: z.string().uuid(),
  closedDateTime: z.custom<moment.Moment>(val => moment.isMoment(val)),
});

type CloseDealWithReasonFormValues = z.infer<typeof closeDealWithReasonFormSchema>;

export const UpdateDealClosureDialog = () => {
  const {
    isOpen,
    handleClose,
    data: { parentId, bpfId, reasonId, closedDateTime, type },
  } = useModal(ModalsKeys.UpdateDealClosureDialog);

  const { loading: dealLifeCycleLoading, actions: dealLifeCycleActions } = useDealLifeCycle();

  const { control, handleSubmit } = useForm<CloseDealWithReasonFormValues>({
    resolver: zodResolver(closeDealWithReasonFormSchema),
    defaultValues: {
      reasonId,
      closedDateTime: moment(closedDateTime),
    },
  });

  const { data: response, loading: closeReasonsLoading } = useQuery(bpfAPI.fetchCloseReasonsOptions, {
    notifyOnNetworkStatusChange: true,
    variables: {
      type,
      id: bpfId,
    },
  });

  const currentReasonId = useWatch({ control, name: 'reasonId' });

  const submit = async (values: CloseDealWithReasonFormValues) => {
    const { reasonId, closedDateTime } = values;

    await dealLifeCycleActions.handleUpdateDealClosure(
      {
        reasonId,
        id: parentId,
        closedDateTime: closedDateTime.toISOString(),
      },
      () => ({
        awaitRefetchQueries: true,
        refetchQueries: [Q_VO],
      })
    );

    handleClose();
  };

  return (
    <CustomDialog
      label="Edit"
      isOpen={isOpen}
      onClose={handleClose}
      actions={
        <Stack direction="row" gap="0.5rem">
          <Button
            size="small"
            variant="contained"
            disabled={!currentReasonId}
            onClick={handleSubmit(submit)}
            loading={dealLifeCycleLoading.isUpdatingDealClosure}
          >
            Save changes
          </Button>

          <Button size="small" onClick={handleClose} disabled={dealLifeCycleLoading.isUpdatingDealClosure}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Typography variant="subtitle2">{`Edit ${type === BpfCloseReasonType.Won ? 'Won' : 'Lost'} reason`}</Typography>

        <SelectField
          required
          label="Reason"
          name="reasonId"
          control={control}
          variant="outlined"
          isLoading={closeReasonsLoading}
          options={response?.bpf?.options}
          disabled={!response?.bpf?.options?.length || dealLifeCycleLoading.isDealClosing}
        />

        <Divider />

        <DateField
          label="Close Date"
          control={control}
          name="closedDateTime"
          slotProps={{
            textField: { required: true, variant: 'outlined', size: 'small' },
          }}
        />
      </Stack>
    </CustomDialog>
  );
};
